/**
 * Teacher's submission history — full timeline edition.
 * Lives inline inside SubmitRecordPage.
 *
 * Two data sources merged via /api/teacher/history:
 *   - submissions: tt_submission_queue rows submittedBy = me
 *   - directRecords: tt_student_record where teacherId = me but not
 *     linked to one of my submissions (NVTT entered on my behalf)
 *
 * Each row is expandable into a detail strip with:
 *   - Vertical timeline of every status transition (đã nộp, tiếp nhận,
 *     đã vào khóa / từ chối / trì hoãn)
 *   - Course / rejection reason / note blocks
 *   - Meta (địa chỉ, ngày sinh, face match)
 *
 * Layout:
 *   Desktop ≥ 600px → table with progress-dot column + expandable detail row
 *   Mobile  < 600px → card list, also expandable
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  teacherHistoryApi,
  teacherDeleteSubmissionApi,
  type TtSubmission,
  type SubmissionStatus,
  type TeacherHistoryDirectRecord,
} from '../../features/trungTam/services/trungTamApi';
import ScanMethodBadge from '../../features/trungTam/components/ScanMethodBadge';

type Props = {
  /** Reload signal — bump from parent after submit/edit/delete. Polling
   *  also keeps the list fresh every 30s. */
  reloadKey?: number;
  onEditSubmission?: (sub: TtSubmission) => void;
  onViewSubmission?: (sub: TtSubmission) => void;
  onViewDirectRecord?: (rec: TeacherHistoryDirectRecord) => void;
};

type FilterKey = 'all' | SubmissionStatus | 'direct';

const STATUS_META: Record<SubmissionStatus, { label: string; cls: string; icon: string }> = {
  pending:  { label: 'Chờ tiếp nhận',     cls: 'ts-status--pending',  icon: 'hourglass_empty' },
  accepted: { label: 'Đã tiếp nhận',      cls: 'ts-status--accepted', icon: 'inventory_2' },
  deferred: { label: 'Trì hoãn khóa sau', cls: 'ts-status--deferred', icon: 'pause_circle' },
  entered:  { label: 'Đã vào khóa',       cls: 'ts-status--entered',  icon: 'check_circle' },
  rejected: { label: 'Bị từ chối',        cls: 'ts-status--rejected', icon: 'cancel' },
};

const formatShortDate = (iso: string | null): string => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  } catch {
    return '—';
  }
};

const resolveSubmissionThumb = (sub: TtSubmission): string | null => {
  const raw = sub.portraitBase64 || sub.avatarBase64;
  if (!raw) return null;
  return raw.startsWith('data:') ? raw : `data:image/jpeg;base64,${raw}`;
};

type TimelineStep = { icon: string; cls: string; label: string; time: string };

/** Build a timeline of status transitions for a submission. The order
 *  matches the natural workflow: submitted → accepted → entered/rejected
 *  /deferred. The last entry shows the current state at the bottom. */
const buildTimeline = (sub: TtSubmission): TimelineStep[] => {
  const steps: TimelineStep[] = [
    { icon: 'send', cls: 'tl--submitted', label: 'Đã nộp', time: formatShortDate(sub.submittedAt) },
  ];
  if (sub.acceptedAt) {
    steps.push({
      icon: 'inventory_2',
      cls: 'tl--accepted',
      label: `Tiếp nhận${sub.acceptor ? ` — ${sub.acceptor.username}` : ''}`,
      time: formatShortDate(sub.acceptedAt),
    });
  }
  if (sub.status === 'entered' && sub.enteredAt) {
    const courseLabel = sub.linkedCourse ? ` → ${sub.linkedCourse.name}` : '';
    steps.push({
      icon: 'check_circle',
      cls: 'tl--entered',
      label: `Đã nhập khóa${courseLabel}${sub.enterer ? ` — ${sub.enterer.username}` : ''}`,
      time: formatShortDate(sub.enteredAt),
    });
  }
  if (sub.status === 'rejected' && sub.rejectedAt) {
    steps.push({
      icon: 'cancel',
      cls: 'tl--rejected',
      label: `Từ chối${sub.rejecter ? ` — ${sub.rejecter.username}` : ''}`,
      time: formatShortDate(sub.rejectedAt),
    });
  }
  if (sub.status === 'deferred' && sub.deferredAt) {
    steps.push({
      icon: 'pause_circle',
      cls: 'tl--deferred',
      label: `Trì hoãn cho khóa sau${sub.deferrer ? ` — ${sub.deferrer.username}` : ''}`,
      time: formatShortDate(sub.deferredAt),
    });
  }
  return steps;
};

const SubmissionHistoryList: React.FC<Props> = ({
  reloadKey, onEditSubmission, onViewSubmission, onViewDirectRecord,
}) => {
  const [submissions, setSubmissions] = useState<TtSubmission[]>([]);
  const [directRecords, setDirectRecords] = useState<TeacherHistoryDirectRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterKey>('all');
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const resp = await teacherHistoryApi();
      if (resp.EC === 0 && resp.DT) {
        setSubmissions(resp.DT.submissions || []);
        setDirectRecords(resp.DT.directRecords || []);
      }
    } catch {
      // silent — polling will retry
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const iv = setInterval(refresh, 30_000);
    return () => clearInterval(iv);
  }, [refresh, reloadKey]);

  const handleRetract = useCallback(async (sub: TtSubmission) => {
    if (sub.status !== 'pending') {
      toast.info('Chỉ có thể rút lại khi hồ sơ đang ở trạng thái "Chờ tiếp nhận"');
      return;
    }
    if (!window.confirm(`Rút lại hồ sơ ${sub.hoTen}?`)) return;
    try {
      const resp = await teacherDeleteSubmissionApi(sub.id);
      if (resp.EC === 0) {
        toast.success('Đã rút lại hồ sơ');
        await refresh();
      } else {
        toast.warn(resp.EM || 'Không rút được');
      }
    } catch (err: unknown) {
      const e = err as { response?: { data?: { EM?: string } } };
      toast.error(e?.response?.data?.EM || 'Lỗi');
    }
  }, [refresh]);

  const counts = useMemo(() => {
    const result: Record<FilterKey, number> = {
      all: submissions.length + directRecords.length,
      pending: submissions.filter(s => s.status === 'pending').length,
      accepted: submissions.filter(s => s.status === 'accepted').length,
      deferred: submissions.filter(s => s.status === 'deferred').length,
      entered: submissions.filter(s => s.status === 'entered').length,
      rejected: submissions.filter(s => s.status === 'rejected').length,
      direct: directRecords.length,
    };
    return result;
  }, [submissions, directRecords]);

  const filteredItems = useMemo(() => {
    if (filter === 'direct') {
      return directRecords.map(r => ({ kind: 'direct' as const, rec: r, sortKey: r.createdAt }));
    }
    if (filter === 'all') {
      const items = [
        ...submissions.map(s => ({ kind: 'submission' as const, sub: s, sortKey: s.submittedAt })),
        ...directRecords.map(r => ({ kind: 'direct' as const, rec: r, sortKey: r.createdAt })),
      ];
      items.sort((a, b) => new Date(b.sortKey).getTime() - new Date(a.sortKey).getTime());
      return items;
    }
    return submissions
      .filter(s => s.status === filter)
      .map(s => ({ kind: 'submission' as const, sub: s, sortKey: s.submittedAt }));
  }, [submissions, directRecords, filter]);

  const toggleExpand = (key: string) => setExpandedKey(prev => prev === key ? null : key);

  return (
    <section className="ts-list">
      <h2>
        <i className="material-icons" style={{ verticalAlign: 'middle', marginRight: 6 }}>history</i>
        Lịch sử đã nộp
      </h2>

      {/* Stat cards — clickable as filters */}
      <div className="ts-stats">
        <button type="button" className={`ts-stat ts-stat--all ${filter === 'all' ? 'ts-stat--active' : ''}`} onClick={() => setFilter('all')}>
          <i className="material-icons">folder_open</i>
          <div><strong>{counts.all}</strong><span>Tất cả</span></div>
        </button>
        <button type="button" className={`ts-stat ts-stat--pending ${filter === 'pending' ? 'ts-stat--active' : ''}`} onClick={() => setFilter('pending')}>
          <i className="material-icons">hourglass_empty</i>
          <div><strong>{counts.pending}</strong><span>Chờ tiếp nhận</span></div>
        </button>
        <button type="button" className={`ts-stat ts-stat--accepted ${filter === 'accepted' ? 'ts-stat--active' : ''}`} onClick={() => setFilter('accepted')}>
          <i className="material-icons">inventory_2</i>
          <div><strong>{counts.accepted}</strong><span>Đã tiếp nhận</span></div>
        </button>
        <button type="button" className={`ts-stat ts-stat--entered ${filter === 'entered' ? 'ts-stat--active' : ''}`} onClick={() => setFilter('entered')}>
          <i className="material-icons">check_circle</i>
          <div><strong>{counts.entered}</strong><span>Đã vào khóa</span></div>
        </button>
        <button type="button" className={`ts-stat ts-stat--rejected ${filter === 'rejected' ? 'ts-stat--active' : ''}`} onClick={() => setFilter('rejected')}>
          <i className="material-icons">cancel</i>
          <div><strong>{counts.rejected}</strong><span>Bị từ chối</span></div>
        </button>
        {counts.direct > 0 && (
          <button type="button" className={`ts-stat ts-stat--accepted ${filter === 'direct' ? 'ts-stat--active' : ''}`} onClick={() => setFilter('direct')}>
            <i className="material-icons">edit_note</i>
            <div><strong>{counts.direct}</strong><span>NVTT nhập giúp</span></div>
          </button>
        )}
      </div>

      {loading ? (
        <p className="ts-empty">Đang tải...</p>
      ) : filteredItems.length === 0 ? (
        <p className="ts-empty">
          {counts.all === 0
            ? 'Chưa có hồ sơ nào. Nộp hồ sơ đầu tiên ở khu trên.'
            : 'Không có hồ sơ nào ở trạng thái này.'}
        </p>
      ) : (
        <>
          {/* Desktop / tablet: table with expandable detail row */}
          <div className="ts-table-wrap">
            <table className="ts-table">
              <thead>
                <tr>
                  <th style={{ width: 28 }}></th>
                  <th style={{ width: 60 }}>STT</th>
                  <th style={{ width: 56 }}></th>
                  <th>Họ tên / CCCD</th>
                  <th>Hạng / Khóa</th>
                  <th>Trạng thái</th>
                  <th>Ngày nộp</th>
                  <th>Tiến trình</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item) => {
                  if (item.kind === 'submission') {
                    const sub = item.sub;
                    const meta = STATUS_META[sub.status];
                    const thumb = resolveSubmissionThumb(sub);
                    const key = `sub-${sub.id}`;
                    const expanded = expandedKey === key;
                    const timeline = buildTimeline(sub);
                    const canEdit = sub.status === 'pending';
                    return (
                      <React.Fragment key={key}>
                        <tr
                          className={`ts-row ${meta.cls} ${expanded ? 'ts-row--expanded' : ''}`}
                          onClick={() => toggleExpand(key)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td>
                            <i className="material-icons ts-expand-icon">
                              {expanded ? 'expand_less' : 'expand_more'}
                            </i>
                          </td>
                          <td>
                            <span className="ts-row__seqno" title={`Số thứ tự trong khóa hạng ${sub.hang}`}>
                              #{sub.seqNo > 0 ? sub.seqNo : '—'}
                            </span>
                          </td>
                          <td>
                            {thumb ? (
                              <img src={thumb} alt="" className="ts-thumb" />
                            ) : (
                              <div className="ts-thumb ts-thumb--placeholder">
                                <i className="material-icons">person</i>
                              </div>
                            )}
                          </td>
                          <td>
                            <div className="ts-row__name">{sub.hoTen}</div>
                            <div className="ts-row__cccd">{sub.soCCCD || '—'}</div>
                            <div className="ts-row__meta">
                              <ScanMethodBadge method={sub.scanMethod} size="compact" />
                            </div>
                          </td>
                          <td>
                            <span className="ts-hang">{sub.hang}</span>
                            {sub.linkedCourse && (
                              <div className="ts-row__sub">{sub.linkedCourse.name}</div>
                            )}
                          </td>
                          <td>
                            <span className={`ts-status ${meta.cls}`}>
                              <i className="material-icons">{meta.icon}</i>
                              {meta.label}
                            </span>
                          </td>
                          <td className="ts-row__date">{formatShortDate(sub.submittedAt)}</td>
                          <td>
                            <div className="ts-progress-dots">
                              {timeline.map((step, i) => (
                                <span key={i} className={`ts-dot ${step.cls}`} title={step.label}>
                                  <i className="material-icons">{step.icon}</i>
                                </span>
                              ))}
                            </div>
                          </td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                              {canEdit && onEditSubmission && (
                                <button
                                  type="button"
                                  className="ts-btn ts-btn--outline ts-btn--sm"
                                  onClick={() => onEditSubmission(sub)}
                                  title="Sửa hồ sơ"
                                >
                                  <i className="material-icons">edit</i>
                                </button>
                              )}
                              {!canEdit && onViewSubmission && (
                                <button
                                  type="button"
                                  className="ts-btn ts-btn--outline ts-btn--sm"
                                  onClick={() => onViewSubmission(sub)}
                                  title="Xem chi tiết"
                                >
                                  <i className="material-icons">visibility</i>
                                </button>
                              )}
                              {canEdit && (
                                <button
                                  type="button"
                                  className="ts-btn ts-btn--outline ts-btn--sm"
                                  onClick={() => handleRetract(sub)}
                                  title="Rút lại"
                                >
                                  <i className="material-icons">delete</i>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {expanded && (
                          <tr className="ts-detail-row">
                            <td colSpan={9}>
                              <div className="ts-detail">
                                <div className="ts-timeline">
                                  <h4>Lịch sử xử lý</h4>
                                  {timeline.map((step, i) => (
                                    <div key={i} className={`ts-tl-step ${step.cls}`}>
                                      <div className="ts-tl-dot">
                                        <i className="material-icons">{step.icon}</i>
                                      </div>
                                      <div className="ts-tl-body">
                                        <span className="ts-tl-label">{step.label}</span>
                                        <span className="ts-tl-time">{step.time}</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                                <div className="ts-detail-info">
                                  {sub.status === 'entered' && sub.linkedCourse && (
                                    <div className="ts-detail-block ts-detail-block--entered">
                                      <i className="material-icons">school</i>
                                      <div>
                                        <strong>Khóa: {sub.linkedCourse.name}</strong>
                                        <span>Hạng {sub.linkedCourse.hang} — {sub.linkedCourse.status === 'open' ? 'Đang mở' : 'Đã đóng'}</span>
                                      </div>
                                    </div>
                                  )}
                                  {sub.status === 'rejected' && (
                                    <div className="ts-detail-block ts-detail-block--rejected">
                                      <i className="material-icons">error</i>
                                      <div>
                                        <strong>Lý do từ chối</strong>
                                        <span>{sub.rejectionReason || 'Không có lý do cụ thể'}</span>
                                      </div>
                                    </div>
                                  )}
                                  {sub.note && (
                                    <div className="ts-detail-block ts-detail-block--note">
                                      <i className="material-icons">sticky_note_2</i>
                                      <div>
                                        <strong>Ghi chú</strong>
                                        <span>{sub.note}</span>
                                      </div>
                                    </div>
                                  )}
                                  <div className="ts-detail-meta">
                                    {sub.diaChi && <span><i className="material-icons">location_on</i>{sub.diaChi}</span>}
                                    {sub.ngaySinh && <span><i className="material-icons">cake</i>{sub.ngaySinh}</span>}
                                    {sub.faceMatchScore != null && (
                                      <span>
                                        <i className="material-icons">face</i>
                                        Khớp khuôn mặt: {sub.faceMatchScore}%
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  }
                  // direct record
                  const rec = item.rec;
                  const key = `direct-${rec.id}`;
                  const expanded = expandedKey === key;
                  return (
                    <React.Fragment key={key}>
                      <tr
                        className={`ts-row ts-status--entered ${expanded ? 'ts-row--expanded' : ''}`}
                        onClick={() => toggleExpand(key)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>
                          <i className="material-icons ts-expand-icon">
                            {expanded ? 'expand_less' : 'expand_more'}
                          </i>
                        </td>
                        <td>
                          <span className="ts-row__seqno" title={`Số thứ tự trong khóa ${rec.course?.name || ''}`}>
                            #{rec.seqNo ?? '—'}
                          </span>
                        </td>
                        <td>
                          <div className="ts-thumb ts-thumb--placeholder">
                            <i className="material-icons">edit_note</i>
                          </div>
                        </td>
                        <td>
                          <div className="ts-row__name">{rec.hoTen}</div>
                          <div className="ts-row__cccd">{rec.soCCCD || '—'}</div>
                          <div className="ts-row__sub">NVTT nhập giúp</div>
                        </td>
                        <td>
                          <span className="ts-hang">{rec.course?.hang || '—'}</span>
                          {rec.course && <div className="ts-row__sub">{rec.course.name}</div>}
                        </td>
                        <td>
                          <span className="ts-status ts-status--entered">
                            <i className="material-icons">check_circle</i>
                            Đã vào khóa
                          </span>
                        </td>
                        <td className="ts-row__date">{formatShortDate(rec.createdAt)}</td>
                        <td>
                          <div className="ts-progress-dots">
                            <span className="ts-dot tl--entered" title="Đã vào khóa">
                              <i className="material-icons">check_circle</i>
                            </span>
                          </div>
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          {onViewDirectRecord && (
                            <button
                              type="button"
                              className="ts-btn ts-btn--outline ts-btn--sm"
                              onClick={() => onViewDirectRecord(rec)}
                              title="Xem chi tiết"
                              style={{ float: 'right' }}
                            >
                              <i className="material-icons">visibility</i>
                            </button>
                          )}
                        </td>
                      </tr>
                      {expanded && (
                        <tr className="ts-detail-row">
                          <td colSpan={9}>
                            <div className="ts-detail">
                              <div className="ts-timeline">
                                <h4>Lịch sử xử lý</h4>
                                <div className="ts-tl-step tl--entered">
                                  <div className="ts-tl-dot">
                                    <i className="material-icons">check_circle</i>
                                  </div>
                                  <div className="ts-tl-body">
                                    <span className="ts-tl-label">
                                      NVTT nhập trực tiếp{rec.enteredByUser ? ` — ${rec.enteredByUser.username}` : ''}
                                    </span>
                                    <span className="ts-tl-time">{formatShortDate(rec.createdAt)}</span>
                                  </div>
                                </div>
                              </div>
                              <div className="ts-detail-info">
                                {rec.course && (
                                  <div className="ts-detail-block ts-detail-block--entered">
                                    <i className="material-icons">school</i>
                                    <div>
                                      <strong>Khóa: {rec.course.name}</strong>
                                      <span>Hạng {rec.course.hang} — {rec.course.status === 'open' ? 'Đang mở' : 'Đã đóng'}</span>
                                    </div>
                                  </div>
                                )}
                                <div className="ts-detail-meta">
                                  {rec.ngaySinh && <span><i className="material-icons">cake</i>{rec.ngaySinh}</span>}
                                  <span><i className="material-icons">verified</i>{rec.reviewed ? 'Đã duyệt' : 'Chưa duyệt'}</span>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile: card list */}
          <ul className="ts-card-list">
            {filteredItems.map((item) => {
              if (item.kind === 'submission') {
                const sub = item.sub;
                const meta = STATUS_META[sub.status];
                const thumb = resolveSubmissionThumb(sub);
                const key = `card-sub-${sub.id}`;
                const expanded = expandedKey === key;
                const timeline = buildTimeline(sub);
                const canEdit = sub.status === 'pending';
                return (
                  <li
                    key={key}
                    className={`ts-card ${meta.cls} ${expanded ? 'ts-card--expanded' : ''}`}
                    onClick={() => toggleExpand(key)}
                  >
                    <div className="ts-card__head">
                      {thumb ? (
                        <img src={thumb} alt="" className="ts-thumb" />
                      ) : (
                        <div className="ts-thumb ts-thumb--placeholder">
                          <i className="material-icons">person</i>
                        </div>
                      )}
                      <div className="ts-card__title">
                        <div className="ts-row__name">
                          {sub.hoTen}
                          {sub.seqNo > 0 && (
                            <span className="ts-row__seqno" title={`STT hạng ${sub.hang}`}>
                              #{sub.seqNo}
                            </span>
                          )}
                        </div>
                        <div className="ts-row__cccd">{sub.soCCCD || '—'}</div>
                        <div className="ts-row__meta">
                          <ScanMethodBadge method={sub.scanMethod} size="compact" />
                        </div>
                      </div>
                      <span className="ts-hang">{sub.hang}</span>
                    </div>

                    <div className="ts-card__row">
                      <span className={`ts-status ${meta.cls}`}>
                        <i className="material-icons">{meta.icon}</i>
                        {meta.label}
                      </span>
                      <span className="ts-card__date">{formatShortDate(sub.submittedAt)}</span>
                    </div>

                    <div className="ts-progress-dots">
                      {timeline.map((step, i) => (
                        <span key={i} className={`ts-dot ${step.cls}`} title={step.label}>
                          <i className="material-icons">{step.icon}</i>
                        </span>
                      ))}
                    </div>

                    {expanded && (
                      <div className="ts-card__detail" onClick={e => e.stopPropagation()}>
                        <div className="ts-timeline">
                          {timeline.map((step, i) => (
                            <div key={i} className={`ts-tl-step ${step.cls}`}>
                              <div className="ts-tl-dot">
                                <i className="material-icons">{step.icon}</i>
                              </div>
                              <div className="ts-tl-body">
                                <span className="ts-tl-label">{step.label}</span>
                                <span className="ts-tl-time">{step.time}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                        {sub.status === 'entered' && sub.linkedCourse && (
                          <div className="ts-detail-block ts-detail-block--entered">
                            <i className="material-icons">school</i>
                            <div>
                              <strong>Khóa: {sub.linkedCourse.name}</strong>
                              <span>Hạng {sub.linkedCourse.hang} — {sub.linkedCourse.status === 'open' ? 'Đang mở' : 'Đã đóng'}</span>
                            </div>
                          </div>
                        )}
                        {sub.status === 'rejected' && (
                          <div className="ts-detail-block ts-detail-block--rejected">
                            <i className="material-icons">error</i>
                            <div>
                              <strong>Lý do từ chối</strong>
                              <span>{sub.rejectionReason || 'Không có lý do cụ thể'}</span>
                            </div>
                          </div>
                        )}
                        {sub.note && (
                          <div className="ts-detail-block ts-detail-block--note">
                            <i className="material-icons">sticky_note_2</i>
                            <div>
                              <strong>Ghi chú</strong>
                              <span>{sub.note}</span>
                            </div>
                          </div>
                        )}
                        {sub.diaChi && (
                          <div className="ts-detail-meta">
                            <span><i className="material-icons">location_on</i>{sub.diaChi}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {(canEdit || !canEdit) && (
                      <div className="ts-card__actions" onClick={(e) => e.stopPropagation()}>
                        {canEdit && onEditSubmission && (
                          <button
                            type="button"
                            className="ts-btn ts-btn--outline"
                            onClick={() => onEditSubmission(sub)}
                          >
                            <i className="material-icons">edit</i>
                            Sửa
                          </button>
                        )}
                        {canEdit && (
                          <button
                            type="button"
                            className="ts-btn ts-btn--outline"
                            onClick={() => handleRetract(sub)}
                          >
                            <i className="material-icons">delete</i>
                            Rút lại
                          </button>
                        )}
                        {!canEdit && onViewSubmission && (
                          <button
                            type="button"
                            className="ts-btn ts-btn--outline"
                            onClick={() => onViewSubmission(sub)}
                          >
                            <i className="material-icons">visibility</i>
                            Xem
                          </button>
                        )}
                      </div>
                    )}
                  </li>
                );
              }
              // direct record card
              const rec = item.rec;
              const key = `card-direct-${rec.id}`;
              const expanded = expandedKey === key;
              return (
                <li
                  key={key}
                  className={`ts-card ts-status--entered ${expanded ? 'ts-card--expanded' : ''}`}
                  onClick={() => toggleExpand(key)}
                >
                  <div className="ts-card__head">
                    <div className="ts-thumb ts-thumb--placeholder">
                      <i className="material-icons">edit_note</i>
                    </div>
                    <div className="ts-card__title">
                      <div className="ts-row__name">
                        {rec.hoTen}
                        {rec.seqNo != null && (
                          <span className="ts-row__seqno">#{rec.seqNo}</span>
                        )}
                      </div>
                      <div className="ts-row__cccd">{rec.soCCCD || '—'}</div>
                      <div className="ts-row__sub">NVTT nhập giúp</div>
                    </div>
                    <span className="ts-hang">{rec.course?.hang || '—'}</span>
                  </div>
                  <div className="ts-card__row">
                    <span className="ts-status ts-status--entered">
                      <i className="material-icons">check_circle</i>
                      Đã vào khóa
                    </span>
                    <span className="ts-card__date">{formatShortDate(rec.createdAt)}</span>
                  </div>
                  {expanded && rec.course && (
                    <div className="ts-card__detail" onClick={e => e.stopPropagation()}>
                      <div className="ts-detail-block ts-detail-block--entered">
                        <i className="material-icons">school</i>
                        <div>
                          <strong>Khóa: {rec.course.name}</strong>
                          <span>Hạng {rec.course.hang} — {rec.course.status === 'open' ? 'Đang mở' : 'Đã đóng'}</span>
                        </div>
                      </div>
                    </div>
                  )}
                  {onViewDirectRecord && (
                    <div className="ts-card__actions" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className="ts-btn ts-btn--outline"
                        onClick={() => onViewDirectRecord(rec)}
                      >
                        <i className="material-icons">visibility</i>
                        Xem
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </section>
  );
};

export default SubmissionHistoryList;
